<template>
  <section class="account-details-panel">
    <p v-if="loading" class="account-details-state">{{ language.t('accountDetails.loading') }}</p>
    <div v-else-if="loadError" class="account-details-error" role="alert">
      <p class="account-details-state error">{{ language.t('accountDetails.loadFailed') }}</p>
      <small>{{ loadErrorDetail }}</small>
      <button type="button" class="ghost-inline-action" :disabled="loading" @click="load">{{ language.t('accountDetails.retry') }}</button>
    </div>
    <template v-else>
      <dl class="account-details-list">
        <div><dt>{{ language.t('accountDetails.id') }}</dt><dd>{{ account.user_id || '—' }}</dd></div>
        <div>
          <dt>{{ language.t('accountDetails.nickname') }}</dt>
          <dd v-if="editingField === 'nickname'" class="account-details-editor">
            <input v-model.trim="profileDraft.nickname" maxlength="64" autocomplete="nickname" :aria-label="language.t('accountDetails.nickname')">
            <div class="account-inline-edit-actions">
              <button type="button" class="ghost-inline-action" :disabled="profileSaving" @click="cancelProfileEdit">{{ language.t('common.cancel') }}</button>
              <button type="button" class="primary-inline-action" :disabled="profileSaving" @click="saveProfile">{{ language.t(profileSaving ? 'accountDetails.saving' : 'common.save') }}</button>
            </div>
          </dd>
          <dd v-else class="account-details-value">
            <span>{{ account.nickname || '—' }}</span>
            <button type="button" class="account-edit-icon" :disabled="profileSaving" :aria-label="language.t('accountDetails.editField', { field: language.t('accountDetails.nickname') })" :title="language.t('accountDetails.editField', { field: language.t('accountDetails.nickname') })" @click="beginProfileEdit('nickname')">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm17.71-10.04a1 1 0 0 0 0-1.41l-2.51-2.51a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 2-1.66Z" /></svg>
            </button>
          </dd>
        </div>
        <div>
          <dt>{{ language.t('accountDetails.account') }}</dt>
          <dd v-if="editingField === 'account'" class="account-details-editor">
            <input v-model.trim="profileDraft.account" maxlength="128" autocomplete="username" :aria-label="language.t('accountDetails.account')">
            <div class="account-inline-edit-actions">
              <button type="button" class="ghost-inline-action" :disabled="profileSaving" @click="cancelProfileEdit">{{ language.t('common.cancel') }}</button>
              <button type="button" class="primary-inline-action" :disabled="profileSaving" @click="saveProfile">{{ language.t(profileSaving ? 'accountDetails.saving' : 'common.save') }}</button>
            </div>
          </dd>
          <dd v-else class="account-details-value">
            <span>{{ account.account || '—' }}</span>
            <small v-if="accountChangeLocked" class="account-cooldown-hint">{{ accountChangeLockHint }}</small>
            <button type="button" class="account-edit-icon" :disabled="profileSaving || accountChangeLocked" :aria-label="accountChangeLocked ? accountChangeLockHint : language.t('accountDetails.editField', { field: language.t('accountDetails.account') })" :title="accountChangeLocked ? accountChangeLockHint : language.t('accountDetails.editField', { field: language.t('accountDetails.account') })" @click="beginProfileEdit('account')">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm17.71-10.04a1 1 0 0 0 0-1.41l-2.51-2.51a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 2-1.66Z" /></svg>
            </button>
          </dd>
        </div>
        <div>
          <dt>{{ language.t('accountDetails.phone') }}</dt>
          <dd v-if="editingField === 'phone'" class="account-details-editor">
            <div class="account-phone-editor"><input v-model.trim="profileDraft.area_code" class="area-code" inputmode="tel" :aria-label="language.t('accountDetails.areaCode')"><input v-model.trim="profileDraft.phone_number" inputmode="numeric" autocomplete="tel-national" :aria-label="language.t('accountDetails.phone')"></div>
            <div class="account-inline-edit-actions">
              <button type="button" class="ghost-inline-action" :disabled="profileSaving" @click="cancelProfileEdit">{{ language.t('common.cancel') }}</button>
              <button type="button" class="primary-inline-action" :disabled="profileSaving" @click="saveProfile">{{ language.t(profileSaving ? 'accountDetails.saving' : 'common.save') }}</button>
            </div>
          </dd>
          <dd v-else class="account-details-value">
            <span>{{ account.phone || '—' }}</span>
            <button type="button" class="account-edit-icon" :disabled="profileSaving" :aria-label="language.t('accountDetails.editField', { field: language.t('accountDetails.phone') })" :title="language.t('accountDetails.editField', { field: language.t('accountDetails.phone') })" @click="beginProfileEdit('phone')">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm17.71-10.04a1 1 0 0 0 0-1.41l-2.51-2.51a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 2-1.66Z" /></svg>
            </button>
          </dd>
        </div>
        <div>
          <dt>{{ language.t('accountDetails.email') }}</dt>
          <dd v-if="editingField === 'email'" class="account-details-editor">
            <input v-model.trim="profileDraft.email" type="email" maxlength="254" autocomplete="email" :aria-label="language.t('accountDetails.email')" @input="resetEmailVerification">
            <template v-if="emailNeedsVerification">
              <div class="account-email-code-actions">
                <button type="button" class="ghost-inline-action" :disabled="emailCodeSending || !emailCodeCanSend" @click="sendEmailVerificationCode">
                  {{ language.t(emailCodeSending ? 'accountDetails.emailCodeSending' : emailCodeSent ? 'accountDetails.emailCodeResend' : 'accountDetails.emailCodeSend') }}
                </button>
                <small v-if="emailCodeSent && emailCodeTarget === normalizedDraftEmail">{{ language.t('accountDetails.emailCodeSent') }}</small>
              </div>
              <input v-if="emailCodeSent && emailCodeTarget === normalizedDraftEmail" v-model.trim="emailVerificationCode" type="text" inputmode="numeric" autocomplete="one-time-code" maxlength="6" :aria-label="language.t('accountDetails.emailCodeLabel')" :placeholder="language.t('accountDetails.emailCodePlaceholder')">
              <p v-if="emailCodeError" class="account-details-state error" role="alert">{{ language.t(emailCodeError) }}</p>
            </template>
            <div class="account-inline-edit-actions">
              <button type="button" class="ghost-inline-action" :disabled="profileSaving" @click="cancelProfileEdit">{{ language.t('common.cancel') }}</button>
              <button type="button" class="primary-inline-action" :disabled="profileSaving || !emailVerificationReady" @click="saveProfile">{{ language.t(profileSaving ? 'accountDetails.saving' : 'common.save') }}</button>
            </div>
          </dd>
          <dd v-else class="account-details-value">
            <span>{{ account.email || '—' }}</span>
            <button type="button" class="account-edit-icon" :disabled="profileSaving" :aria-label="language.t('accountDetails.editField', { field: language.t('accountDetails.email') })" :title="language.t('accountDetails.editField', { field: language.t('accountDetails.email') })" @click="beginProfileEdit('email')">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm17.71-10.04a1 1 0 0 0 0-1.41l-2.51-2.51a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 2-1.66Z" /></svg>
            </button>
          </dd>
        </div>
        <div class="account-details-referrer">
          <dt>{{ language.t('accountDetails.referrer') }}</dt>
          <dd v-if="referrer">
            <strong>{{ referrer.nickname || referrer.account || referrer.user_id }}</strong>
            <small>{{ referrer.account || referrer.user_id }}</small>
          </dd>
          <dd v-else class="account-details-empty">
            <span>{{ language.t('accountDetails.notSet') }}</span>
            <button v-if="!referrerEditing" type="button" class="account-edit-icon" :aria-label="language.t('accountDetails.editField', { field: language.t('accountDetails.referrer') })" :title="language.t('accountDetails.editField', { field: language.t('accountDetails.referrer') })" @click="beginReferrerEdit">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm17.71-10.04a1 1 0 0 0 0-1.41l-2.51-2.51a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 2-1.66Z" /></svg>
            </button>
          </dd>
        </div>
      </dl>

      <p v-if="profileError" class="account-details-state error" role="alert">{{ language.t(profileError) }}</p>
      <p v-if="profileSaved && !editingField" class="account-details-state success" role="status">{{ language.t('accountDetails.profileSaveSuccess') }}</p>

      <p v-if="referrerSaved && !referrerEditing" class="account-details-state success" role="status">{{ language.t('accountDetails.saveSuccess') }}</p>
      <div v-if="referrerEditing" class="account-referrer-editor">
        <label>
          <span>{{ language.t('accountDetails.searchPlaceholder') }}</span>
          <input v-model.trim="keyword" maxlength="128" autocomplete="off" @keydown.enter.prevent="search">
        </label>
        <button type="button" class="ghost-inline-action" :disabled="searching || !keyword" @click="search">{{ searching ? language.t('accountDetails.searching') : language.t('accountDetails.search') }}</button>
        <p v-if="searchError" class="account-details-state error" role="alert">{{ language.t(searchError) }}</p>
        <p v-else-if="searched && !matches.length" class="account-details-state">{{ language.t('accountDetails.noMatches') }}</p>
        <div v-if="matches.length" class="account-referrer-results" role="listbox" :aria-label="language.t('accountDetails.referrer')">
          <button v-for="person in matches" :key="person.userID" type="button" role="option" :aria-selected="selected?.userID === person.userID" :class="{ selected: selected?.userID === person.userID }" @click="selected = person">
            <strong>{{ person.nickname || person.account || person.userID }}</strong>
            <small>{{ person.account || person.userID }}<template v-if="person.phoneHint"> · {{ person.phoneHint }}</template></small>
          </button>
        </div>
        <p v-if="selected" class="account-details-state">{{ language.t('accountDetails.selected', { name: selected.nickname || selected.account || selected.userID }) }}</p>
        <p v-if="saveError" class="account-details-state error" role="alert">{{ language.t(saveError) }}</p>
        <div class="account-referrer-actions">
          <button type="button" class="ghost-inline-action" :disabled="saving" @click="cancelReferrerEdit">{{ language.t('common.cancel') }}</button>
          <button type="button" class="primary-inline-action" :disabled="saving || !selected" @click="saveReferrer">{{ language.t(saving ? 'accountDetails.saving' : 'accountDetails.save') }}</button>
        </div>
      </div>
      <p v-if="referrer" class="account-details-locked">{{ language.t('accountDetails.locked') }}</p>
    </template>
  </section>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useSessionStore } from '@/stores/session';
import { useLanguageStore } from '@/stores/language';
import { accountDetailsRequest } from '@/services/accountDetails';

const session = useSessionStore();
const language = useLanguageStore();
const loading = ref(true);
const loadError = ref(false);
const loadErrorDetail = ref('');
const account = ref({});
const referrer = ref(null);
const editingField = ref('');
const profileSaving = ref(false);
const profileSaved = ref(false);
const profileError = ref('');
const profileDraft = reactive({ nickname: '', account: '', area_code: '+86', phone_number: '', email: '' });
const emailVerificationCode = ref('');
const emailChallengeID = ref('');
const emailCodeTarget = ref('');
const emailCodeSent = ref(false);
const emailCodeSending = ref(false);
const emailCodeError = ref('');
const normalizedDraftEmail = computed(() => String(profileDraft.email || '').trim().toLowerCase());
const normalizedCurrentEmail = computed(() => String(account.value.email || '').trim().toLowerCase());
const emailNeedsVerification = computed(() => normalizedDraftEmail.value !== normalizedCurrentEmail.value);
const emailCodeRecipient = computed(() => normalizedDraftEmail.value || normalizedCurrentEmail.value);
const emailCodeCanSend = computed(() => emailNeedsVerification.value && isValidEmail(emailCodeRecipient.value));
const emailVerificationReady = computed(() => !emailNeedsVerification.value || (emailChallengeID.value && emailCodeTarget.value === normalizedDraftEmail.value && /^\d{6}$/.test(emailVerificationCode.value)));
const accountChangeLocked = computed(() => Boolean(account.value.account_change_available_at));
const accountChangeLockHint = computed(() => {
  const availableAt = account.value.account_change_available_at;
  if (!availableAt) return '';
  const date = new Date(availableAt);
  const formattedDate = Number.isNaN(date.getTime()) ? availableAt : date.toLocaleString();
  return language.t('accountDetails.accountChangeCooldownUntil', { date: formattedDate });
});
const referrerEditing = ref(false);
const keyword = ref('');
const matches = ref([]);
const selected = ref(null);
const searching = ref(false);
const saving = ref(false);
const searched = ref(false);
const referrerSaved = ref(false);
const searchError = ref('');
const saveError = ref('');

onMounted(load);

async function load() {
  loading.value = true;
  loadError.value = false;
  loadErrorDetail.value = '';
  try {
    const data = await accountDetailsRequest(session.token);
    account.value = data.account || {};
    referrer.value = data.referrer || null;
  } catch (error) {
    loadError.value = true;
    const code = typeof error?.code === 'string' ? error.code : 'network_error';
    loadErrorDetail.value = `${error?.status ? `HTTP ${error.status} · ` : ''}${code}`;
  } finally {
    loading.value = false;
  }
}

function beginProfileEdit(field) {
  if (field === 'account' && accountChangeLocked.value) return;
  profileDraft.nickname = account.value.nickname || '';
  profileDraft.account = account.value.account || '';
  profileDraft.area_code = account.value.area_code || '+86';
  profileDraft.phone_number = account.value.phone_number || '';
  profileDraft.email = account.value.email || '';
  resetEmailVerification();
  editingField.value = field;
  profileSaved.value = false;
  profileError.value = '';
}

function cancelProfileEdit() { editingField.value = ''; profileError.value = ''; resetEmailVerification(); }

function isValidEmail(value) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim()); }

function resetEmailVerification() {
  emailVerificationCode.value = '';
  emailChallengeID.value = '';
  emailCodeTarget.value = '';
  emailCodeSent.value = false;
  emailCodeError.value = '';
}

async function sendEmailVerificationCode() {
  if (emailCodeSending.value || !emailCodeCanSend.value) return;
  emailCodeSending.value = true;
  emailCodeError.value = '';
  try {
    const data = await accountDetailsRequest(session.token, 'send_email_change_code', { email: normalizedDraftEmail.value, remove_email: !normalizedDraftEmail.value });
    emailChallengeID.value = data.challenge_id || '';
    emailCodeTarget.value = normalizedDraftEmail.value;
    emailCodeSent.value = Boolean(emailChallengeID.value);
    emailVerificationCode.value = '';
  } catch (error) {
    emailCodeError.value = ({
      email_already_registered: 'accountDetails.emailAlreadyRegistered',
      email_code_retry_later: 'accountDetails.emailCodeRetryLater',
      email_code_rate_limit: 'accountDetails.emailCodeRateLimit',
      email_code_send_failed: 'accountDetails.emailCodeSendFailed',
      email_unchanged: 'accountDetails.emailUnchanged',
      invalid_request: 'accountDetails.profileInvalid'
    })[error?.code] || 'accountDetails.emailCodeSendFailed';
  } finally {
    emailCodeSending.value = false;
  }
}

async function saveProfile() {
  if (profileSaving.value) return;
  profileSaving.value = true;
  profileError.value = '';
  profileSaved.value = false;
  try {
    const emailVerification = emailNeedsVerification.value ? { email_challenge_id: emailChallengeID.value, email_verification_code: emailVerificationCode.value } : {};
    const data = await accountDetailsRequest(session.token, 'update_profile', { ...profileDraft, ...emailVerification });
    account.value = data.account || account.value;
    referrer.value = data.referrer || null;
    session.displayName = account.value.nickname || session.displayName;
    profileSaved.value = true;
    editingField.value = '';
  } catch (error) {
    profileError.value = ({
      phone_already_registered: 'accountDetails.phoneAlreadyRegistered',
      email_already_registered: 'accountDetails.emailAlreadyRegistered',
      email_verification_required: 'accountDetails.emailVerificationRequired',
      email_verification_invalid: 'accountDetails.emailVerificationInvalid',
      account_already_registered: 'accountDetails.accountAlreadyRegistered',
      account_change_cooldown: 'accountDetails.accountChangeCooldown',
      invalid_request: 'accountDetails.profileInvalid',
      account_unavailable: 'accountDetails.saveFailed'
    })[error?.code] || 'accountDetails.saveFailed';
  } finally {
    profileSaving.value = false;
  }
}

function beginReferrerEdit() {
  referrerEditing.value = true;
  keyword.value = '';
  matches.value = [];
  selected.value = null;
  searched.value = false;
  referrerSaved.value = false;
  searchError.value = '';
  saveError.value = '';
}

function cancelReferrerEdit() { referrerEditing.value = false; }

async function search() {
  if (!keyword.value || searching.value) return;
  searching.value = true;
  searchError.value = '';
  saveError.value = '';
  selected.value = null;
  searched.value = false;
  try {
    const data = await accountDetailsRequest(session.token, 'search_referrer', { keyword: keyword.value });
    matches.value = Array.isArray(data.matches) ? data.matches : [];
    searched.value = true;
  } catch {
    searchError.value = 'accountDetails.searchFailed';
  } finally {
    searching.value = false;
  }
}

async function saveReferrer() {
  if (!selected.value || saving.value) return;
  saving.value = true;
  saveError.value = '';
  referrerSaved.value = false;
  try {
    const data = await accountDetailsRequest(session.token, 'set_referrer', { referrer_user_id: selected.value.userID });
    account.value = data.account || account.value;
    referrer.value = data.referrer || null;
    referrerSaved.value = true;
    referrerEditing.value = false;
  } catch (error) {
    saveError.value = error.code === 'referrer_locked' ? 'accountDetails.locked' : 'accountDetails.saveFailed';
    if (error.code === 'referrer_locked') await load();
  } finally {
    saving.value = false;
  }
}
</script>

<style scoped>
.account-details-panel { display: grid; gap: 16px; min-width: 0; }
.account-details-list { display: grid; gap: 0; margin: 0; border: 1px solid var(--line); border-radius: 8px; overflow: hidden; }
.account-details-list > div { display: grid; grid-template-columns: minmax(88px, 0.38fr) minmax(0, 1fr); gap: 12px; padding: 13px 14px; border-bottom: 1px solid var(--line); }
.account-details-list > div:last-child { border-bottom: 0; }
.account-details-list dt { color: var(--muted); }
.account-details-list dd { min-width: 0; display: grid; gap: 4px; margin: 0; overflow-wrap: anywhere; }
.account-details-list dd.account-details-value { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 8px; }
.account-details-value > span { min-width: 0; overflow-wrap: anywhere; }
.account-details-value .account-cooldown-hint { grid-column: 1 / -1; color: var(--muted); font-size: 12px; line-height: 1.45; }
.account-details-list dd small { color: var(--muted); }
.account-details-list input { width: 100%; min-width: 0; padding: 8px 10px; border: 1px solid var(--line); border-radius: 7px; background: var(--panel); color: var(--text); }
.account-details-editor { grid-template-columns: minmax(0, 1fr); }
.account-details-list .account-phone-editor { display: grid; grid-template-columns: minmax(72px, .42fr) minmax(0, 1fr); gap: 7px; }
.account-email-code-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.account-email-code-actions small { color: var(--muted); }
.account-inline-edit-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; }
.account-edit-icon { display: inline-grid; place-items: center; flex: 0 0 34px; width: 34px; height: 34px; margin: -6px -7px -6px 0; padding: 7px; border: 0; border-radius: 7px; background: transparent; color: var(--primary-strong); cursor: pointer; }
.account-edit-icon:hover:not(:disabled) { background: rgba(0, 0, 0, .06); }
.account-edit-icon:focus-visible { outline: 2px solid var(--primary-strong); outline-offset: 1px; }
.account-edit-icon:disabled { cursor: wait; opacity: .55; }
.account-edit-icon svg { display: block; width: 18px; height: 18px; fill: currentColor; }
.account-details-empty { display: flex !important; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px !important; }
.account-referrer-editor { display: grid; gap: 10px; }
.account-referrer-editor label { display: grid; gap: 6px; color: var(--muted); font-size: 13px; }
.account-referrer-editor input { width: 100%; min-width: 0; padding: 10px 12px; border: 1px solid var(--line); border-radius: 8px; background: var(--panel); color: var(--text); }
.account-referrer-results { display: grid; max-height: 220px; overflow: auto; border: 1px solid var(--line); border-radius: 8px; }
.account-referrer-results button { display: grid; gap: 4px; padding: 11px 12px; border: 0; border-bottom: 1px solid var(--line); background: var(--panel); color: var(--text); text-align: left; overflow-wrap: anywhere; }
.account-referrer-results button:last-child { border-bottom: 0; }
.account-referrer-results button.selected { background: #e5f5f1; color: var(--primary-strong); }
.account-referrer-results small,.account-details-locked { color: var(--muted); }
.account-referrer-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 8px; }
.account-details-state { margin: 0; color: var(--muted); line-height: 1.5; overflow-wrap: anywhere; }
.account-details-state.error { color: #b42318; }
.account-details-error { display: grid; justify-items: start; gap: 8px; }
.account-details-error small { color: var(--muted); overflow-wrap: anywhere; }
.account-details-state.success { color: var(--primary-strong); }
.account-details-locked { margin: 0; font-size: 13px; }
@media(max-width: 420px) { .account-details-list > div { grid-template-columns: 78px minmax(0,1fr); padding: 11px 10px; } }
</style>
